<template>
  <section class="transactions">
    <div class="transactions__header">
      <div class="transactions__info">
        <div class="transactions__icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.66667 15L6.66667 10L10.8333 14.1667L18.3333 6.66667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.3333 6.66667H18.3333V11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <h2 class="transactions__title">API Transactions</h2>
          <p class="transactions__subtitle">Recent transactions by your organisation and its members</p>
        </div>
      </div>
      <router-link :to="{ name: 'ApiClientTransactions' }" class="btn-secondary">
        <span>See All Transactions</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.33334 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 3.33337L12.6667 8.00004L8 12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </router-link>
    </div>
    
    <div class="transactions__content">
      <div v-if="transactions && transactions.length > 0">
        <TransactionTable
          :tableRows="tableRows"
          :tableHeaders="tableHeaders"
          :columnNumber="1"
          :totalPage="transactionsPages"
          :currentPage="transactionPage"
          :column-width="150"
          :show-pagination="false"
        />
      </div>
      <EmptyState v-else>
        <template v-slot:message>
          <div class="empty-message">
            <div class="empty-message__icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.66667 24L10.6667 16L17.3333 22.6667L29.3333 10.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M21.3333 10.6667H29.3333V18.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="empty-message__title">No transactions yet</h3>
            <p class="empty-message__text">
              Your transactions will show up here when you fund, withdraw or purchase tokens.
            </p>
          </div>
        </template>
      </EmptyState>
    </div>
  </section>
</template>

<script>
import EmptyState from "@/components/notification/EmptyState";
import TransactionTable from "@/components/tables/TransactionTable";
import {
  formatAmountToDollar,
  formatISODate,
  formatISOTime,
  removeUnderscore,
  truncate,
} from "@/utils/helpers.js";
import { mapActions, mapGetters } from "vuex";
  export default {
    name: "ApiClientTransactions",
    data() {
      return {
        tableHeaders: ["", "Details", "Reference", "Amount", "Price", "Status"],
      };
    },
    components: {
      TransactionTable,
      EmptyState,
    },
    watch: {
      "$store.state.ApiClient.currency"() {
        this.fetchTransactions(1);
      },
    },
    created() {
      this.fetchTransactions(1);
    },
    computed: {
      ...mapGetters("ApiClient", [
        "transactions",
        "transactionsPages",
        "transactionPage",
      ]),
      tableRows() {
        return this.transactions.map((transaction) => {
          let type = removeUnderscore(transaction.type);
          switch (type) {
            case "Debit Sell Wallet":
              type = "Debit Wallet";
              break;

            case "Fund Sell Wallet":
              type = "Fund Wallet";
              break;
            default:
              break;
          }

          let name = `${transaction.token.name}`;
          switch (name) {
            case "GetEquity USD Token":
              name = "USD";
              break;
            default:
              break;
          }
          let volume = transaction.volume;
          let details;
          if (type === "Plan") {
            details = truncate(`${type}: ${transaction.price} ${name}`, 30);
          } else {
            details = truncate(`${type}: ${volume} ${name}`, 30);
          }
          let amount;
          if (transaction.price) {
            amount = transaction.price * transaction.volume;
          } else {
            amount = transaction.token?.price.buy * transaction.volume;
          }
          return {
            details: `<span class="tw-font-bold">${details}</span> <br />
          <span class="tw-text-black-light tw-text-xs">${formatISODate(
            transaction.createdAt
          )}  ${formatISOTime(transaction.createdAt)}</span>`,
            reference:
              type === "Fee" ||
              type === "Debit Wallet" ||
              type === "Send" ||
              type === "Withdraw" ||
              type === "Sell" ||
              type === "Plan"
                ? `<span class="tw-text-xs tw-mb-1">${truncate(
                    transaction.reference,
                    25
                  )}</span> <br />
          <span class="tw-text-xs tw-text-error">Debit</span>`
                : `<span class="tw-text-xs tw-mb-1">${truncate(
                    transaction.reference,
                    25
                  )}</span> <br />
          <span class="tw-text-xs tw-text-success">Credit</span>`,
            amount: formatAmountToDollar(amount, 2, "USD"),
            price: transaction.price
              ? formatAmountToDollar(transaction.price, 2, "USD")
              : "N/A",
            type: `<span class="tw-hidden">${type}</span>`,
            status: transaction.status,
          };
        });
      },
    },
    methods: {
      ...mapActions("ApiClient", ["getTransactions"]),
      async fetchTransactions(page) {
        try {
          await this.getTransactions({ page, limit: 10 });
        } catch (error) {
          return error;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .transactions {
    padding: 32px;
    
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 20px;
      flex-wrap: wrap;
      padding-bottom: 24px;
      border-bottom: 1px solid #E2E8F0;
      margin-bottom: 24px;
    }
    
    &__info {
      display: flex;
      align-items: flex-start;
      gap: 14px;
    }
    
    &__icon {
      width: 40px;
      height: 40px;
      background: #EEF4FF;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #4D84FF;
      flex-shrink: 0;
    }
    
    &__title {
      font-family: 'DM Sans', sans-serif;
      font-size: 16px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 4px 0;
    }
    
    &__subtitle {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
    
    &__content {
      background: #ffffff;
    }
  }
  
  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: #F1F5F9;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: #E2E8F0;
    }
    
    svg {
      transition: transform 0.2s ease;
    }
    
    &:hover svg {
      transform: translateX(4px);
    }
  }
  
  .empty-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 48px 24px;
    
    &__icon {
      width: 64px;
      height: 64px;
      background: #F1F5F9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #64748B;
      margin-bottom: 16px;
    }
    
    &__title {
      font-family: 'DM Sans', sans-serif;
      font-size: 16px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 8px 0;
    }
    
    &__text {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
      max-width: 320px;
    }
  }
  
  @media (max-width: 640px) {
    .transactions {
      padding: 20px;
      
      &__header {
        flex-direction: column;
      }
      
      .btn-secondary {
        width: 100%;
        justify-content: center;
      }
    }
  }
</style>
